/**
 * Module: Widget Search Filter Types
 * SpecReferenceID: WIDGET-V1
 * Requirements: WIDGET-V1-R27
 *
 * Shared types, constants, and request mapping for the widget's faceted,
 * capability-driven search refine controls. Ported from the web app
 * (FRONTEND-WEB-V1 search-filter-types) so the widget speaks the exact same
 * server-side filter contract on the /api/v1/search/* endpoints. Filtering is
 * applied server-side; these types describe the user's selections and how they
 * map onto the API request. Framework-agnostic (no React / no UI deps).
 */

/** A facet bucket returned by the backend: a value and its document count. */
export interface FacetValue {
  value: string;
  count: number;
}

/** An available facet field and its values (WIDGET-V1-R27). */
export interface Facet {
  field: string;
  label: string;
  values: FacetValue[];
}

/**
 * The user's active refine selections.
 *
 * `facetSelections` maps an index field (e.g. `GuidelineType`,
 * `RecommendationStrength`, `ShortTitle`) to the chosen values. `recommendationsOnly`
 * is a one-click convenience toggle; selecting any `RecommendationStrength` value
 * implies it (the coupling rule is applied in the panel).
 */
export interface SearchFilters {
  recommendationsOnly: boolean;
  prioritizeRecent: boolean;
  dateFrom: string;
  dateTo: string;
  facetSelections: Record<string, string[]>;
  /** Simple-search only: scope the query to the FiguresAndTables field. */
  limitToFiguresAndTables: boolean;
}

export const INITIAL_FILTERS: SearchFilters = {
  recommendationsOnly: false,
  prioritizeRecent: false,
  dateFrom: '',
  dateTo: '',
  facetSelections: {},
  limitToFiguresAndTables: false,
};

/** The recommendation-class facet field (drives the coupling rule). */
export const RECOMMENDATION_FACET_FIELD = 'RecommendationStrength';

/** Facet fields rendered as a searchable typeahead (high cardinality). */
export const TYPEAHEAD_FACET_FIELDS: readonly string[] = ['ShortTitle'];

/** Date-range quick presets (relative to today). `months`/`years` set the lower bound. */
export interface DatePreset {
  label: string;
  months?: number;
  years?: number;
}

export const DATE_PRESETS: readonly DatePreset[] = [
  { label: 'Last 6 months', months: 6 },
  { label: 'Last 12 months', months: 12 },
  { label: 'Last 5 years', years: 5 },
];

/**
 * Compute an ISO `YYYY-MM-DD` lower bound for a preset, relative to today.
 * Uses UTC date math so the result is timezone-stable (the date input is
 * date-only and tz-agnostic).
 */
export function presetDateFrom(preset: DatePreset, now: Date = new Date()): string {
  const d = new Date(now);
  if (preset.years) d.setUTCFullYear(d.getUTCFullYear() - preset.years);
  if (preset.months) d.setUTCMonth(d.getUTCMonth() - preset.months);
  return d.toISOString().slice(0, 10);
}

/** Sentinel for the "Any time" preset option. */
export const ANY_TIME_PRESET_VALUE = '__any_time__';

/**
 * Resolve a preset-dropdown value to a `{ from, to }` date range (To = today).
 * "Any time" clears the range; an unknown value returns null.
 */
export function presetRange(
  value: string,
  now: Date = new Date(),
): { from: string; to: string } | null {
  if (value === ANY_TIME_PRESET_VALUE) return { from: '', to: '' };
  const preset = DATE_PRESETS.find((p) => p.label === value);
  if (!preset) return null;
  return { from: presetDateFrom(preset, now), to: now.toISOString().slice(0, 10) };
}

/** True when any refine control is active (drives the "Clear all" affordance). */
export function hasActiveFilters(filters: SearchFilters): boolean {
  return (
    filters.recommendationsOnly ||
    filters.prioritizeRecent ||
    filters.limitToFiguresAndTables ||
    filters.dateFrom !== '' ||
    filters.dateTo !== '' ||
    Object.values(filters.facetSelections).some((vals) => vals.length > 0)
  );
}

/**
 * Map the UI selections onto the server-side filter request fields.
 *
 * Facet selections become `"Field:Value"` strings. The COR-class coupling is
 * already reflected in `recommendationsOnly`/`facetSelections` by the panel, so
 * the backend filter-builder drops the redundant `RecommendationId` clause when a
 * class is selected.
 */
export interface SearchFilterRequest {
  facets: string[];
  date_from: string | null;
  date_to: string | null;
  recommendations_only: boolean;
}

export function toFilterRequest(filters: SearchFilters): SearchFilterRequest {
  const facets: string[] = [];
  for (const [field, values] of Object.entries(filters.facetSelections)) {
    for (const value of values) {
      facets.push(`${field}:${value}`);
    }
  }
  return {
    facets,
    date_from: filters.dateFrom || null,
    date_to: filters.dateTo || null,
    recommendations_only: filters.recommendationsOnly,
  };
}
